import React, { useEffect, useState } from 'react';
import apiUrls from '../../api/apiUrls';
import fetchWithAuth from '../../api/fetchWithAuth';
import { useLang } from "../../contexts/LanguageContext";
import { Modal, Button } from 'react-bootstrap';
import DendriticNode from './DendriticNode';
import './LemmaModal.css';

const formatError = (err) => {
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    if (err.message) return err.message;
    if (err.error) return formatError(err.error);
    try { return JSON.stringify(err); } catch { return String(err); }
  }
  return String(err);
};

const LemmaModal = ({ show, handleClose, langOrigen, dictLanguage, lemmaId, lemmaInfor }) => {
  const { t } = useLang();

  const [lemmaData, setLemmaData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('standard'); // 'standard' o 'dendritic'

  useEffect(() => {
    const shouldFetch = show && (lemmaId !== null && lemmaId !== undefined && lemmaId !== '');
    const controller = new AbortController();
    const signal = controller.signal;

    if (!shouldFetch) {
      setLemmaData(null);
      setError(null);
      setIsLoading(false);
      return () => controller.abort();
    }

    const fetchLemmaData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const finalDict = `${langOrigen}_${dictLanguage || langOrigen}`;
        const url = `${apiUrls.apiUrlLemma}${finalDict}/${lemmaId}`;
        const response = await fetchWithAuth(url, { signal });

        if (!response.ok) {
          if (response.status === 404) {
            setError(t('LEMMA_MODAL_NOT_FOUND'));
          } else {
            setError(`${t('LEMMA_MODAL_API_ERROR')} (${response.status})`);
          }
          setLemmaData(null);
          return;
        }

        const data = await response.json();

        if (data?.error) {
          const apiErrStr = formatError(data.error);
          if (apiErrStr === 'LEMMA_NOT_FOUND') {
            setError(t('LEMMA_MODAL_NOT_FOUND'));
          } else {
            setError(apiErrStr || t('LEMMA_MODAL_API_ERROR'));
          }
          setLemmaData(null);
          return;
        }

        const payload = data?.data ?? data;
        const isEmpty = !payload || (typeof payload === 'object' && Object.keys(payload).length === 0);
        if (isEmpty) {
          setError(t('LEMMA_MODAL_NOT_FOUND'));
          setLemmaData(null);
        } else {
          setLemmaData(payload);
          setError(null);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error al consultar la API:', err);
        const errStr = formatError(err);
        setError(errStr === 'LEMMA_NOT_FOUND' ? t('LEMMA_MODAL_NOT_FOUND') : (errStr || t('LEMMA_MODAL_NETWORK_ERROR')));
        setLemmaData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLemmaData();

    return () => controller.abort();
  }, [show, lemmaId, langOrigen, dictLanguage, t]);

  useEffect(() => {
    if (lemmaData?.dendritic) {
      setViewMode('dendritic');
    } else {
      setViewMode('standard');
    }
  }, [lemmaData]);

  const closeAndCleanup = (resumeAudio = false) => {
    handleClose(resumeAudio);
    setLemmaData(null);
    setError(null);
  };

  const handleModalHide = () => closeAndCleanup(false);
  const handleContinueClick = () => closeAndCleanup(true);

  // Render helpers
  const renderDendriticExplorer = () => {
    if (!lemmaData?.dendritic) return null;
    const { root_morpheme, core_meaning, data_tree, language } = lemmaData.dendritic;
    const isBilingual = langOrigen !== dictLanguage;

    return (
      <div className="dendritic-explorer">
        <div className="root-header mb-4 p-3 bg-dark rounded border-primary border">
          <h4 className="morfema-title text-primary">
            <span className="badge bg-primary me-2">{t("LEMMA_MODAL_ROOT")} [{language?.toUpperCase()}]</span> {root_morpheme}
          </h4>
          <p className="core-meaning text-white mb-0" style={{fontStyle: 'italic'}}>"{core_meaning}"</p>
        </div>
        <div className="dendritic-tree-container">
           {data_tree.map((node, idx) => (
             <DendriticNode 
                key={idx} 
                node={node} 
                targetWord={lemmaData.word} 
                dictLanguage={dictLanguage}
                targetPOS={lemmaInfor}
                isBilingual={isBilingual}
             />
           ))}
        </div>
      </div>
    );
  };

  const renderPhonetics = (phon) => {
    if (!phon) return null;
    if (Array.isArray(phon)) {
      return phon.flatMap((item, index) => {
        if (item && typeof item === 'object') {
          return Object.entries(item).map(([dialCode, phonetic], i) => (
            <div key={`p-o-${index}-${i}`} className="phon-block">
              <span className="phon">/{phonetic}/</span>
              <span className="phon-dialect">{t(dialCode)}</span>
            </div>
          ));
        }
        return (
          <div key={`p-s-${index}`} className="phon-block">
            <span className="phon">/{String(item)}/</span>
          </div>
        );
      });
    } else {
      return (
        <div className="phon-block">
          <span className="phon">/{String(phon)}/</span>
        </div>
      );
    }
  };

  const parseJsonData = () => {
    const value = lemmaData?.json_data;
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const renderDefinitionsFromJsonData = () => {
    const isBilingual = langOrigen !== dictLanguage;
    const getLangCode = (code) => {
      const map = { 'eng': 'en', 'fra': 'fr', 'ita': 'it', 'deu': 'de', 'por': 'pt', 'esp': 'es' };
      return map[code?.toLowerCase()] || code;
    };
    const targetLang = getLangCode(dictLanguage);

    const jsonData = parseJsonData();
    if (!Array.isArray(jsonData)) return null;

    const blocks = [];
    jsonData.forEach((entry, entryIdx) => {
      if (!entry || typeof entry !== 'object') return;
      Object.entries(entry).forEach(([lemmaKey, lemmaObj], kIdx) => {
        const etys = Array.isArray(lemmaObj?.etymologies) ? lemmaObj.etymologies : [];
        etys.forEach((ety, eIdx) => {
          if (ety?.etymology) {
            blocks.push(
              <div className="etymology-box" key={`ety-${entryIdx}-${kIdx}-${eIdx}`}>
                <span className="etymology-icon">⇒</span>
                <span>{ety.etymology}</span>
              </div>
            );
          }

          const defs = Array.isArray(ety?.definitions) ? ety.definitions : [];
          if (defs.length > 0) {
            blocks.push(
              <div className="definition-block" key={`defs-${lemmaKey}-${entryIdx}-${kIdx}-${eIdx}`}>
                <ol className="number-first-level">
                  {defs.map((def, dIdx) => {
                    const translation = targetLang ? def.translations?.[targetLang] : null;
                    return (
                      <li key={def.number ?? `def-${entryIdx}-${kIdx}-${eIdx}-${dIdx}`}>
                        <div className="meaning-first-level">
                          {def.partOfSpeech && (
                            <span className={`pos-badge pos-${def.partOfSpeech.toLowerCase().replace(/[^a-z]/g, '')}`}>
                              {def.partOfSpeech}
                            </span>
                          )}
                          {translation ? (
                            <span className="meaning-text-node">
                              <strong>{translation}</strong>
                              {!isBilingual && <small className="text-muted ms-2">({def.meaning})</small>}
                            </span>
                          ) : (
                            def.meaning && <span className="meaning-text-node">{def.meaning}</span>
                          )}
                        </div>
                        {def.example && <div className="example-box">"{def.example}"</div>}
                        {Array.isArray(def.subdefinitions) && def.subdefinitions.length > 0 && (
                          <ol className="number-second-level">
                            {def.subdefinitions.map((sd, sIdx) => {
                              const subTrans = targetLang ? sd.translations?.[targetLang] : null;
                              return (
                                <li key={sd.number ?? `sub-${entryIdx}-${kIdx}-${eIdx}-${dIdx}-${sIdx}`}>
                                  {subTrans ? (
                                    <span className="meaning-second-level">
                                      <strong>{subTrans}</strong>
                                      {!isBilingual && <small className="text-muted ms-2">({sd.meaning})</small>}
                                    </span>
                                  ) : (
                                    sd.meaning && <span className="meaning-second-level">{sd.meaning}</span>
                                  )}
                                  {sd.example && <div className="example-box">"{sd.example}"</div>}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          }
        });
      });
    });

    if (blocks.length === 0) return null;
    return <div className="definitions">{blocks}</div>;
  };

  return (
    <Modal show={show} onHide={handleModalHide} contentClassName="premium-lemma-modal">
      <Modal.Header closeButton title={t("LEMMA_MODAL_JUST_CLOSE")} data-bs-theme="dark">
        <Modal.Title>
          {lemmaData?.word || (isLoading ? t("LEMMA_MODAL_LOADING") : t("LEMMA_MODAL_NO_DATA"))}
          <div className="dict-badge-row">
            <span className="lang-badge">{langOrigen}</span>
            <span className="lang-connector">→</span>
            <span className="lang-badge">{dictLanguage}</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <p className="text-danger">{error}</p>}
        {!error && !lemmaData && !isLoading && <p>{t('LEMMA_MODAL_NOT_FOUND')}</p>}
        
        {lemmaData && (
          <>
            {lemmaData.dendritic && lemmaData.json_data && (
              <div className="d-flex justify-content-end mb-3">
                <div className="view-selector-pills">
                  <button 
                    type="button" 
                    className={`view-selector-btn ${viewMode === 'standard' ? 'active' : ''}`}
                    onClick={() => setViewMode('standard')}
                  >
                    {t("LEMMA_MODAL_VIEW_PLANAR")}
                  </button>
                  <button 
                    type="button" 
                    className={`view-selector-btn ${viewMode === 'dendritic' ? 'active' : ''}`}
                    onClick={() => setViewMode('dendritic')}
                  >
                    {t("LEMMA_MODAL_VIEW_FRACTAL")}
                  </button>
                </div>
              </div>
            )}

            {viewMode === 'dendritic' ? (
              renderDendriticExplorer()
            ) : (
              <>
                <div className="phon-container">
                  {lemmaData.dial && <div className="phon-block"><span className="phon">{lemmaData.dial}</span></div>}
                  {renderPhonetics(lemmaData.phon)}
                </div>
                {renderDefinitionsFromJsonData() || (
                  <div className="definitions">
                    {Array.isArray(lemmaData.definitions) && (
                      <ol className="number-first-level">
                        {lemmaData.definitions.map((def, index) => (
                          <li key={index}>
                            <div className="meaning-first-level">
                              {def.partOfSpeech && (
                                <span className={`pos-badge pos-${def.partOfSpeech.toLowerCase().replace(/[^a-z]/g, '')}`}>
                                  {def.partOfSpeech}
                                </span>
                              )}
                              {def.meaning && <span className="meaning-text-node">{def.meaning}</span>}
                            </div>
                            {def.example && <div className="example-box">"{def.example}"</div>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleContinueClick} className="rounded-pill px-4">
          {t("LEMMA_MODAL_CLOSE")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default LemmaModal;
